document.addEventListener('DOMContentLoaded', () => {
  gsap.registerPlugin(ScrollTrigger, ScrollSmoother);

  // Menu functionality
  const menuButton = document.getElementById('menu-button');
  const menu = document.getElementById('menu');

  if (menuButton && menu) {
    // Set initial state
    gsap.set(menu, { height: 0 });

    // Helper functions
    const getLabels = () => ({
      menuLabel: menuButton.querySelector('.menu-label'),
      closeLabel: menuButton.querySelector('.close-label'),
    });

    const toggleLabels = (isOpen) => {
      const { menuLabel, closeLabel } = getLabels();
      if (menuLabel) menuLabel.style.display = isOpen ? 'none' : 'block';
      if (closeLabel) closeLabel.style.display = isOpen ? 'block' : 'none';
    };

    const closeMenu = () => {
      gsap.to(menu, {
        height: 0,
        duration: 0.75,
        ease: 'power2.inOut',
      });
      menu.classList.remove('active');
      menuButton.classList.remove('active');
      toggleLabels(false);
    };

    const openMenu = () => {
      gsap.to(menu, {
        height: 'auto',
        duration: 0.75,
        ease: 'power2.inOut',
      });
      menu.classList.add('active');
      menuButton.classList.add('active');
      toggleLabels(true);
    };

    const toggleMenu = () => {
      if (menu.classList.contains('active')) {
        closeMenu();
      } else {
        openMenu();
      }
    };

    // Event listeners
    menuButton.addEventListener('click', toggleMenu);

    // Close menu when clicking outside
    document.addEventListener('click', (event) => {
      if (menu.classList.contains('active')) {
        const isClickInsideMenu = menu.contains(event.target);
        const isClickInsideHeader = document.querySelector('header').contains(event.target);

        if (!isClickInsideMenu && !isClickInsideHeader) {
          closeMenu();
        }
      }
    });
  }

  // Base Carousel System
  class Carousel {
    constructor(carouselElement, options = {}) {
      this.carousel = carouselElement;
      this.track = carouselElement.querySelector('.carousel-track');
      this.options = {
        axis: options.axis || 'y', // 'x' for horizontal, 'y' for vertical
        gap: options.gap || 16, // 1rem gap
        speed: options.speed || 25, // pixels per second
        defaultItemSize: options.defaultItemSize || 200,
        ...options,
      };

      // Check if track exists before proceeding
      if (!this.track) {
        console.warn(
          `${this.options.axis === 'x' ? 'Horizontal' : 'Vertical'} carousel track not found:`,
          carouselElement
        );
        return;
      }

      this.items = this.track.querySelectorAll('.carousel-item');
      this.animation = null;
      this.isInitialized = false;

      this.init();
    }

    init() {
      // Check if track exists
      if (!this.track) {
        return;
      }

      if (this.isInitialized) {
        this.destroy();
      }

      // Wait for images to load to get accurate dimensions
      this.waitForImages().then(() => {
        this.setupCarousel();
        this.startAnimation();
        this.isInitialized = true;
      });
    }

    waitForImages() {
      // Check if items exist
      if (!this.items || this.items.length === 0) {
        return Promise.resolve();
      }

      const imagePromises = Array.from(this.items).map((item) => {
        const img = item.querySelector('img');
        if (img && !img.complete) {
          return new Promise((resolve) => {
            img.onload = resolve;
            img.onerror = resolve; // Continue even if image fails
          });
        }
        return Promise.resolve();
      });

      return Promise.all(imagePromises);
    }

    setupCarousel() {
      // Check if track and items exist
      if (!this.track || !this.items || this.items.length === 0) {
        return;
      }

      // Remove existing clones
      const existingClones = this.track.querySelectorAll('.carousel-item[data-cloned]');
      existingClones.forEach((clone) => clone.remove());

      // Clone items for seamless loop
      this.items.forEach((item) => {
        const clone = item.cloneNode(true);
        clone.setAttribute('data-cloned', 'true');
        this.track.appendChild(clone);
      });
    }

    startAnimation() {
      // Kill existing animation
      if (this.animation) {
        this.animation.kill();
      }

      // Get current dimensions
      const itemSize =
        this.options.axis === 'x'
          ? this.items[0]?.offsetWidth || this.options.defaultItemSize
          : this.items[0]?.offsetHeight || this.options.defaultItemSize;

      const totalItemSize = itemSize + this.options.gap;
      const totalSize = this.items.length * totalItemSize;

      // Check if carousel should reverse (only for vertical)
      const isReverse = this.options.axis === 'y' && this.carousel.classList.contains('reverse');

      // Set initial position based on direction
      const initialPosition = isReverse ? -totalSize : 0;
      gsap.set(this.track, { [this.options.axis]: initialPosition });

      // Create infinite scroll animation
      this.animation = gsap.to(this.track, {
        [this.options.axis]: isReverse ? 0 : -totalSize,
        duration: totalSize / this.options.speed,
        ease: 'none',
        repeat: -1,
        onUpdate: () => {
          // Reset position when animation completes one cycle
          if (this.animation.progress() >= 1) {
            gsap.set(this.track, { [this.options.axis]: initialPosition });
          }
        },
      });
    }

    destroy() {
      if (this.animation) {
        this.animation.kill();
        this.animation = null;
      }
      this.isInitialized = false;
    }

    resize() {
      // Debounce resize events
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => {
        this.init();
      }, 250);
    }
  }

  // Carousel instances
  const carousels = [];

  // Initialize vertical carousels
  const verticalCarouselElements = document.querySelectorAll('.vertical-carousel');
  verticalCarouselElements.forEach((carouselElement) => {
    const carousel = new Carousel(carouselElement, { axis: 'y' });
    carousels.push(carousel);
  });

  // Initialize horizontal carousels
  const horizontalCarouselElements = document.querySelectorAll('.horizontal-carousel');
  horizontalCarouselElements.forEach((carouselElement) => {
    const carousel = new Carousel(carouselElement, { axis: 'x', defaultItemSize: 300 });
    carousels.push(carousel);
  });

  // Handle window resize with intelligent detection
  let resizeTimeout;
  let lastWidth = window.innerWidth;
  let lastHeight = window.innerHeight;

  window.addEventListener('resize', () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => {
      const currentWidth = window.innerWidth;
      const currentHeight = window.innerHeight;

      // Only trigger resize if width changed significantly (ignore height changes from mobile UI)
      const widthChanged = Math.abs(currentWidth - lastWidth) > 10; // 10px threshold

      if (widthChanged) {
        carousels.forEach((carousel) => carousel.resize());
        lastWidth = currentWidth;
        lastHeight = currentHeight;
      }
    }, 250); // Increased debounce time for better performance
  });

  // Card swiper scripts

  document.querySelectorAll('.card-swiper').forEach((swiperEl) => {
    // Find navigation buttons only within this swiper instance
    const nextEl = swiperEl.querySelector('.swiper-next');
    const prevEl = swiperEl.querySelector('.swiper-prev');
    const scrollbarEl = swiperEl.querySelector('.swiper-scrollbar');

    new Swiper(swiperEl, {
      slidesPerView: 'auto',
      freeMode: true,
      spaceBetween: 16,
      breakpoints: {
        1280: {
          slidesPerView: 4,
          spaceBetween: 0,
        },
      },
      navigation: {
        nextEl: nextEl,
        prevEl: prevEl,
      },
      scrollbar: {
        el: scrollbarEl,
        draggable: true,
      },
    });
  });

  // .item-swiper: no pagination
  document.querySelectorAll('.item-swiper').forEach((swiperEl) => {
    // Find navigation buttons only within this swiper instance
    const nextEl = swiperEl.querySelector('.swiper-next');
    const prevEl = swiperEl.querySelector('.swiper-prev');
    const scrollbarEl = swiperEl.querySelector('.swiper-scrollbar');

    new Swiper(swiperEl, {
      slidesPerView: 'auto',
      freeMode: true,
      spaceBetween: 8,
      breakpoints: {
        640: {
          spaceBetween: 16,
        },
      },
      navigation: {
        nextEl: nextEl,
        prevEl: prevEl,
      },
      scrollbar: {
        el: scrollbarEl,
        draggable: true,
      },
      // No pagination for .item-swiper
    });
  });

  // .step-swiper: with pagination
  document.querySelectorAll('.step-swiper').forEach((swiperEl) => {
    // Find navigation buttons only within this swiper instance
    const nextEl = swiperEl.querySelector('.swiper-next');
    const prevEl = swiperEl.querySelector('.swiper-prev');
    const scrollbarEl = swiperEl.querySelector('.swiper-scrollbar');

    new Swiper(swiperEl, {
      slidesPerView: 'auto',
      freeMode: true,
      spaceBetween: 16,
      breakpoints: {
        640: {
          spaceBetween: 16,
        },
      },
      navigation: {
        nextEl: nextEl,
        prevEl: prevEl,
      },
      pagination: {
        el: swiperEl.querySelector('.pagination') || undefined,
        clickable: true,
        renderBullet: function (index, className) {
          return '<span class="' + className + '">' + (index + 1) + '</span>';
        },
        bulletClass: 'bullet',
        bulletActiveClass: 'bullet-active',
      },
    });
  });

  // .hero-swiper: with pagination
  document.querySelectorAll('.hero-swiper').forEach((swiperEl) => {
    // Find navigation buttons only within this swiper instance
    const nextEl = swiperEl.querySelector('.swiper-next');
    const prevEl = swiperEl.querySelector('.swiper-prev');

    new Swiper(swiperEl, {
      slidesPerView: 1,

      navigation: {
        nextEl: nextEl,
        prevEl: prevEl,
      },
      pagination: {
        el: swiperEl.querySelector('.pagination') || undefined,
        clickable: true,
        renderBullet: function (index, className) {
          return '<span class="' + className + '">' + (index + 1) + '</span>';
        },
        bulletClass: 'bullet',
        bulletActiveClass: 'bullet-active',
      },
    });
  });

  // Tab System for Multiple Groups
  const tabGroups = document.querySelectorAll('.tab-group');

  tabGroups.forEach((group) => {
    const tabButtons = group.querySelectorAll('.tab-button');
    const tabContents = group.querySelectorAll('.tab');

    // Set initial state for this group
    tabContents.forEach((tab) => {
      if (!tab.classList.contains('active')) {
        gsap.set(tab, { autoAlpha: 0 });
      }
    });

    // Add click handlers for this group
    tabButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const target = button.getAttribute('data-target');
        const targetTab = group.querySelector(`#${target}-tab`);

        if (!targetTab) return;

        // Update button states in this group only
        tabButtons.forEach((btn) => btn.classList.remove('active'));
        button.classList.add('active');

        // Animate tabs in this group only
        tabContents.forEach((tab) => {
          if (tab === targetTab) {
            gsap.to(tab, { autoAlpha: 1 });
            tab.classList.add('active');
          } else {
            gsap.to(tab, { autoAlpha: 0 });
            tab.classList.remove('active');
          }
        });
      });
    });
  });

  // Paywall functionality
  const publication = document.getElementById('publication');
  const paywall = document.getElementById('paywall');

  if (publication && paywall) {
    // Set initial state - hide paywall
    gsap.set(paywall, {
      opacity: 0,
      pointerEvents: 'none',
    });

    // Calculate 1/4 of publication height
    const publicationHeight = publication.offsetHeight;
    const triggerPoint = publicationHeight * 0.33;

    // Create ScrollTrigger for paywall
    ScrollTrigger.create({
      trigger: publication,
      start: `top+=${triggerPoint} bottom`,
      end: 'bottom top',
      onEnter: () => {
        // Show paywall with animation
        gsap.to(paywall, {
          opacity: 1,
          ease: 'power2.out',
          pointerEvents: 'auto',
        });
      },
      onLeave: () => {
        // Hide paywall when scrolling back up
        gsap.to(paywall, {
          opacity: 0,
          ease: 'power2.in',
          pointerEvents: 'none',
        });
      },
      onEnterBack: () => {
        // Show paywall again when scrolling back down
        gsap.to(paywall, {
          opacity: 1,
          duration: 0.6,
          ease: 'power2.out',
          pointerEvents: 'auto',
        });
      },
      onLeaveBack: () => {
        // Hide paywall when scrolling back up above trigger point
        gsap.to(paywall, {
          opacity: 0,
          duration: 0.4,
          ease: 'power2.in',
          pointerEvents: 'none',
        });
      },
    });
  }

  // SHOPIFY SPECIFIC FEAUTRES //
  // cart scripts //
  const openCart = document.getElementById('open-cart');
  const cart = document.getElementById('cart');
  const closeCart = document.getElementById('close-cart');

  if (cart && openCart && closeCart) {
    openCart.addEventListener('click', function () {
      gsap.to(cart, {
        x: '0%',
        onStart: function () {
          cart.classList.add('shadow-lg');
        },
      });
    });
    closeCart.addEventListener('click', function () {
      gsap.to(cart, {
        x: '100%',
        onComplete: function () {
          cart.classList.remove('shadow-lg');
        },
      });
    });
  }

  // search scripts //
  const openSearch = document.getElementById('open-search');
  const search = document.getElementById('search');
  const closeSearch = document.getElementById('close-search');

  if (search) {
    openSearch.addEventListener('click', function () {
      gsap.to(search, {
        x: '0%',
        onStart: function () {
          search.classList.add('shadow-lg');
        },
      });
    });
    closeSearch.addEventListener('click', function () {
      gsap.to(search, {
        x: '100%',
        onComplete: function () {
          search.classList.remove('shadow-lg');
        },
      });
    });
  }

  // qr code //
  const qrCodeContainers = document.querySelectorAll('[data-qr_code]');
  if (qrCodeContainers && qrCodeContainers.length) {
    qrCodeContainers.forEach((container) => {
      const altText = container.dataset.hasOwnProperty('alt') ? container.dataset.alt : '';
      const qrWidth = container.dataset.hasOwnProperty('width') ? parseInt(container.dataset.width) : 128;
      const qrHeight = container.dataset.hasOwnProperty('height') ? parseInt(container.dataset.height) : 128;
      new QRCode(container, {
        text: container.dataset.qr_code,
        width: qrWidth,
        height: qrHeight,
        imageAltText: altText,
      });
    });
  }

  // copy text buttons //
  const copyTextButtons = document.querySelectorAll('[data-copy_text]');
  if (copyTextButtons && copyTextButtons.length) {
    const successClassname = 'success';
    const buttonCooldown = 5000;
    copyTextButtons.forEach((button) => {
      const textToCopy = button.dataset.copy_text;
      const defaultLabel = button.textContent;
      const successLabel = button.dataset.hasOwnProperty('success_label')
        ? button.dataset.success_label
        : 'Text copied';
      button.addEventListener('click', function (event) {
        event.preventDefault();
        button.disabled = true;
        navigator.clipboard.writeText(textToCopy).then(
          function () {
            button.textContent = 'button clicked success';
            button.classList.add(successClassname);
            button.textContent = successLabel;
            setTimeout(() => {
              button.classList.remove(successClassname);
              button.textContent = defaultLabel;
              button.disabled = false;
            }, buttonCooldown);
          },
          function (error) {
            button.textContent = 'button clicked failed';
            button.disabled = false;
          }
        );
      });
    });
  }
});
